// src/Store/labTestSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import BaseUrl from '../../BaseUrl';

const LABS_API_BASE_URL = BaseUrl.labsurl
const PENDING_STATUS_ID = 1; 
const COMPLETED_STATUS_ID = 2;

export const fetchPendingServices = createAsyncThunk(
  'labTests/fetchPendingServices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${LABS_API_BASE_URL}PatientServices/GetServicesByStatus?status=${PENDING_STATUS_ID}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchPendingPackages = createAsyncThunk(
  'labTests/fetchPendingPackages',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${LABS_API_BASE_URL}PatientPackages/GetPackagesByStatus?status=${PENDING_STATUS_ID}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchLabTestServicesMaster = createAsyncThunk(
  'labTests/fetchLabTestServicesMaster',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${LABS_API_BASE_URL}LabTestServices`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchLabTestPackagesMaster = createAsyncThunk(
  'labTests/fetchLabTestPackagesMaster',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${LABS_API_BASE_URL}Packages`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchPackageServicesMaster = createAsyncThunk(
  'labTests/fetchPackageServicesMaster',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${LABS_API_BASE_URL}PackageServices`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const submitLabTestResults = createAsyncThunk(
    'labTests/submitLabTestResults',
    async (payload, { rejectWithValue, dispatch }) => {
        const { mrn,itemType, resultsData, consultationId, bookingId, itemId } = payload;
        
        try {
            if (itemType === 'services') {
                const serviceUpdatePayload = resultsData.map(resultEntry => ({
                    consultationId: consultationId,
                    serviceId: resultEntry.serviceId,
                    mrn:mrn,
                    result: resultEntry.resultValue, // Use resultValue from frontend state
                    // notes: resultEntry.notes, 
                    serviceStatus: COMPLETED_STATUS_ID, // Use status 2 (completed)
                }));
                
                const response = await axios.put(
                    `${LABS_API_BASE_URL}PatientServices`,
                    serviceUpdatePayload
                );
                
                return response.data;

            } else if (itemType === 'packages') {
                const packageUpdatePayload = resultsData.map(resultEntry => ({
                    consultationId: consultationId,
                    packageId: itemId, // packageId is needed here
                    serviceId: resultEntry.serviceId,
                    result: resultEntry.resultValue,
                    mrn:mrn,
                    // notes: resultEntry.notes,
                    status: COMPLETED_STATUS_ID, // Use status 2 (completed)
                }));
                
                const response = await axios.put(
                    `${LABS_API_BASE_URL}PatientPackages`,
                    packageUpdatePayload
                );
                
                return response.data;
            }

            return rejectWithValue('Invalid item type for submission.');

        } catch (error) {
            console.error("API Submission Error:", error.response.data);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);
export const searchServices = createAsyncThunk(
  'patientSearch/searchServices',
  async (searchTerm, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${LABS_API_BASE_URL}PatientServices/GetServicesByMRNPhoneName?Val=${searchTerm}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const searchPackages = createAsyncThunk(
  'patientSearch/searchPackages',
  async (searchTerm, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${LABS_API_BASE_URL}PatientPackages/GetPackagesByMRNPhoneName?Val=${searchTerm}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const initialState = {
  // Pending Data
  pendingServices: [],
  pendingPackages: [],
  loading: 'idle', // 'idle' | 'pending' | 'succeeded' | 'failed'
  error: null,
  activeTab: 'services', // 'services' or 'packages'
  
  // Master Data
  labTestServicesMaster: [],
  labTestPackagesMaster: [],
  packageServicesMaster: [],
  masterLoading: 'idle', // Separate loading state for master data
  masterError: null,
};

const labTestSlice = createSlice({
  name: 'labTests',
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    // Reducer to handle opening the result modal (optional, but good practice)
    openResultModal: (state, action) => {
      state.resultModal = { 
        isOpen: true, 
        item: action.payload.item, 
        type: action.payload.type // 'services' or 'packages'
      };
    },
    closeResultModal: (state) => {
      state.resultModal = { isOpen: false, item: null, type: null };
    }
  },
 extraReducers: (builder) => {
    builder
      // 1. --- Pending Services (addCase) ---
      .addCase(fetchPendingServices.pending, (state) => { state.loading = 'pending'; })
      .addCase(fetchPendingServices.fulfilled, (state, action) => { state.loading = 'succeeded'; state.pendingServices = action.payload; state.error = null; })
      .addCase(fetchPendingServices.rejected, (state, action) => { state.loading = 'failed'; state.error = action.payload; })

      // 2. --- Pending Packages (addCase) ---
      .addCase(fetchPendingPackages.pending, (state) => { state.loading = 'pending'; })
      .addCase(fetchPendingPackages.fulfilled, (state, action) => { state.loading = 'succeeded'; state.pendingPackages = action.payload; state.error = null; })
      .addCase(fetchPendingPackages.rejected, (state, action) => { state.loading = 'failed'; state.error = action.payload; })

      // 3. --- Master Data - Fulfilled/Rejected (addCase) ---
      
      // Lab Test Services Master
      .addCase(fetchLabTestServicesMaster.fulfilled, (state, action) => { state.labTestServicesMaster = action.payload; })
      .addCase(fetchLabTestServicesMaster.rejected, (state, action) => { state.masterLoading = 'failed'; state.masterError = action.payload; })
      
      // Lab Test Packages Master
      .addCase(fetchLabTestPackagesMaster.fulfilled, (state, action) => { state.labTestPackagesMaster = action.payload; })
      .addCase(fetchLabTestPackagesMaster.rejected, (state, action) => { state.masterLoading = 'failed'; state.masterError = action.payload; })

      // Package Services Master (and final loading check)
      .addCase(fetchPackageServicesMaster.fulfilled, (state, action) => { 
        state.packageServicesMaster = action.payload; 
        // Set masterLoading to succeeded only after the last master data fetch succeeds
        if(state.masterLoading === 'pending') {
            state.masterLoading = 'succeeded';
            state.masterError = null;
        }
      })
      .addCase(searchServices.pending, (state) => { state.loading = 'pending'; })
      .addCase(searchServices.fulfilled, (state, action) => { 
        state.loading = 'succeeded'; 
        state.pendingServices = action.payload; // Overwrite for display
        state.error = null; 
      })
      .addCase(searchServices.rejected, (state, action) => { 
        state.loading = 'failed'; 
        state.error = action.payload; 
      })

      // 4. --- Search Packages (Will overwrite pendingPackages for display) ---
      .addCase(searchPackages.pending, (state) => { state.loading = 'pending'; })
      .addCase(searchPackages.fulfilled, (state, action) => { 
        state.loading = 'succeeded'; 
        state.pendingPackages = action.payload; // Overwrite for display
        state.error = null; 
      })
      .addCase(searchPackages.rejected, (state, action) => { 
        state.loading = 'failed'; 
        state.error = action.payload; 
      })
      .addCase(fetchPackageServicesMaster.rejected, (state, action) => { state.masterLoading = 'failed'; state.masterError = action.payload; })
      
      // 4. --- Master Data - Loading all at once (addMatcher) ---
      // This block MUST come after ALL addCase calls.
      .addMatcher(
        (action) => [
          fetchLabTestServicesMaster.pending.type,
          fetchLabTestPackagesMaster.pending.type,
          fetchPackageServicesMaster.pending.type,
        ].includes(action.type),
        (state) => { 
            // Only set to pending if not already failed from a previous attempt
            if (state.masterLoading !== 'failed') {
                state.masterLoading = 'pending'; 
            }
        }
      );
  },
});

export const { setActiveTab, openResultModal, closeResultModal } = labTestSlice.actions;

export default labTestSlice.reducer;